import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { ColorVariant, CornerRadius, Drawer, useMtwContext, useRtl } from '@makyoapp/makyo-tw-ui'

import { ViewComponent } from '@/core/app-builder/app/component'
import { useFrameContext } from '@/lib/react-frame'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useLoadTableIds } from '@/hooks/__project__/useLoadTableId'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { EVENT_KEYS } from '@/shared/custom-event'
import { BuilderOverlay, OverlayPlacement } from '@/shared/overlay'

import { BuilderComponentCoreProps } from './BuilderComponentCore'

export interface BuilderOverlayCoreProps {
  children: (props: BuilderComponentCoreProps) => React.ReactNode
}

/**
 * Builder Overlay Core
 */
export const BuilderOverlayCore = ({ children }: BuilderOverlayCoreProps) => {
  const { activeOverlays } = useAppOverlaysContext()
  const { document: frameDoc } = useFrameContext()
  const { globalDoc } = useMtwContext()
  const [portalReady, setPortalReady] = useState(window.appMode !== 'preview')

  // make sure the global document is set before rendering the overlay
  useLayoutEffect(() => {
    if (window.appMode !== 'preview') return

    if (globalDoc === frameDoc) {
      setPortalReady(true)
    }

    return () => {
      setPortalReady(false)
    }
  }, [frameDoc, globalDoc])

  if (!portalReady) return null

  return activeOverlays.map((overlay) => (
    <RenderOverlay key={overlay.id} overlay={overlay}>
      {(componentProps) => children(componentProps)}
    </RenderOverlay>
  ))
}

interface RenderOverlayProps {
  overlay: BuilderOverlay
  children: (props: BuilderComponentCoreProps) => React.ReactNode
}

/**
 * Render Overlay
 */
const RenderOverlay = ({ overlay, children }: RenderOverlayProps) => {
  // access contexts
  const { dbClient, language, userSpecific, loadedTables, wasmProcessing } = useDbClientContext()
  const { appClient, setActiveOverlays, setActiveComponent, setPageAction } = useAppBuilderContext()
  const { setOverlayAction } = useAppOverlaysContext()
  const [colorScheme] = useBuilderColorSchemeV2()
  const { device } = useBreakpoint()
  const dir = useRtl()

  // derived values
  const isDetailOverlay = !!overlay.tableId
  const detailRowId = overlay.rowId
  const placement = getPlacement(overlay.placement[device], dir)
  const opacity = getOpacity(overlay.opacity)
  const size = overlay.size[device]
  const widthSize = size.split(',')[0] ?? 9999 //default from Vaul drawer 512px
  const cornerRadius = overlay.shape[device] as CornerRadius
  const bgColor = overlay.bgColor as ColorVariant

  // get container client
  const containerClient = isDetailOverlay ? appClient.pagesMap.get(overlay.id) : appClient.overlaysMap.get(overlay.id)
  const detailTableId = containerClient?.settings._TableID

  // load table ids for the overlay
  useLoadTableIds(containerClient)

  // todo
  // usePageActions(page)

  const detailTable = detailTableId ? dbClient?.tablesMap.get(detailTableId) : undefined

  const detailRowIndex = useMemo(() => {
    if (!detailTable || !detailRowId || !loadedTables.has(detailTable.id)) return undefined
    return detailTable.GetRowIndexByRowId(detailRowId, language, userSpecific)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTable, detailRowId, language, userSpecific, loadedTables, wasmProcessing])

  const rowDataNotFound = !!detailTableId && !!detailRowId && detailRowIndex === -1

  // local state
  const [open, setOpen] = useState(true)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleClose = useCallback(() => {
    setOpen(false)
    setPageAction(undefined)
    setOverlayAction(undefined)
    setActiveComponent(undefined)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setActiveOverlays((prev) => prev.filter((o) => o.id && o.id !== overlay.id))
    }, 300)
  }, [overlay.id, setActiveComponent, setActiveOverlays, setOverlayAction, setPageAction])

  useEffect(() => {
    const handleCloseEvent = (e: Event) => {
      if ((e as CustomEvent).detail === overlay.id) {
        handleClose()
      }
    }

    window.addEventListener(EVENT_KEYS.OVERLAY_CLOSE, handleCloseEvent as EventListener)

    return () => {
      window.removeEventListener(EVENT_KEYS.OVERLAY_CLOSE, handleCloseEvent as EventListener)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [handleClose, overlay.id])

  // Register overlay elements in the container client's component map so they can render
  useEffect(() => {
    if (!containerClient) return

    const register = (els: any[], parentId?: string) => {
      for (const el of els) {
        // find if the component is already registered
        const isRegistered = containerClient.componentsMap.has(el.id)
        const alreadyHasSettings = containerClient.settings._BodySettings?.[el.id]
        if (isRegistered && alreadyHasSettings) {
          continue
        }

        // ensure settings entry exists
        containerClient.settings._BodySettings = {
          ...(containerClient.settings._BodySettings || {}),
          [el.id]: overlay.elementSettings?.[el.id] || {},
        }

        // register component for this element
        const body = {
          _id: el.id,
          _ComponentID: el.compId,
          _GroupID: el.groupId,
          _Title: el.customTitle || '',
          _Body: [],
          _Draggable: true,
          _Removable: true,
          _IsSubmit: !!el.isSubmit,
          _Actions: {},
          _Behavior: { _Metadata: {}, _Visibility: { _Type: '', _Clauses: [] }, _UserControlledFilter: {} },
        } as any

        containerClient.componentsMap.set(el.id, new ViewComponent(containerClient, body, parentId))

        if (Array.isArray(el.children) && el.children.length > 0) {
          register(el.children, el.id)
        }
      }
    }

    register(overlay.elements)
  }, [containerClient, overlay.elements, overlay.elementSettings])

  if (!containerClient) return null

  return (
    <Drawer
      id={overlay.id}
      open={open}
      onClose={handleClose}
      placement={placement}
      overrideWidth={widthSize}
      size={size}
      cornerRadius={cornerRadius}
      bgColor={bgColor}
      backdropColor={colorScheme.current === 'dark' ? 'neutral-100' : 'neutral-0'}
      backdropOpacity={opacity}
      style={{ border: 'none' }}
    >
      <div ref={wrapperRef} className='overlay-content flex-1 overflow-auto'>
        <div
          className='flex flex-col'
          style={{ alignItems: { start: 'flex-start', center: 'center', end: 'flex-end' }[overlay.alignment] }}
        >
          {rowDataNotFound ? (
            <>
              {window.appMode === 'public' ? (
                <div className='p-8'>
                  <h1 className='text-lg font-bold'>
                    <Trans>Details not visible</Trans>
                  </h1>
                  <p className='mt-2'>
                    <Trans>ROW_DATA_NOT_FOUND_PUBLIC</Trans>
                  </p>
                </div>
              ) : (
                <div className='p-8'>
                  <Trans>ROW_DATA_NOT_FOUND_BUILDER</Trans>
                </div>
              )}
            </>
          ) : (
            overlay.elements.map((el, i) => {
              return (
                <React.Fragment key={el.id}>
                  {children({
                    containerClient,
                    element: el,
                    rowId: detailRowId,
                    detailRowIndex,
                    index: i,
                  })}
                </React.Fragment>
              )
            })
          )}
        </div>
      </div>
    </Drawer>
  )
}

function getOpacity(val?: number) {
  if (typeof val !== 'number') return 0.3
  if (val > 0 && val < 1) return val
  return val / 100
}

function getPlacement(value: OverlayPlacement, dir: string) {
  let placement = value

  if (dir === 'rtl') {
    if (placement === 'left') {
      placement = 'right'
    }
    if (placement === 'right') {
      placement = 'left'
    }
  }

  return placement
}
